// GolfNow Tee Time Service
// Fetches real tee times from GolfNow via edge function

import { supabase } from '../lib/supabase';
import { analyseTimeSlot } from './roundService';
import { toLocalDateKey } from '../utils/dateHelpers';

/**
 * Fetch tee times from GolfNow for a facility
 * @param {number|string} facilityId - GolfNow facility ID
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {Object} options - Optional parameters
 * @param {number} [options.players=0] - Number of players (0 = any)
 * @param {number} [options.holes=3] - Holes filter (1=9, 2=18, 3=both). Prefer omitting to fetch all tee times.
 * @param {string} [options.region='US'] - Region ('US', 'UK', 'EU', 'AU')
 * @returns {Promise<Object>} Response with teeTimes array and summary
 */
export async function getGolfNowTeeTimes(facilityId, startDate, endDate, options = {}) {
  const { players = 0, holes = 3, region = 'US' } = options;

  // Ensure facilityId is a number
  const numericFacilityId = typeof facilityId === 'string'
    ? parseInt(facilityId, 10)
    : facilityId;

  if (isNaN(numericFacilityId)) {
    console.error('[GolfNow] Invalid facilityId:', facilityId);
    return { success: false, error: 'Invalid facility ID', teeTimes: [], summary: null };
  }

  // Format dates to YYYY-MM-DD
  const formattedStartDate = formatDateToYYYYMMDD(startDate);
  const formattedEndDate = formatDateToYYYYMMDD(endDate);

  if (!formattedStartDate || !formattedEndDate) {
    console.error('[GolfNow] Invalid dates:', { startDate, endDate });
    return { success: false, error: 'Invalid date format', teeTimes: [], summary: null };
  }

  console.log('[GolfNow] Fetching tee times:', {
    facilityId: numericFacilityId,
    startDate: formattedStartDate,
    endDate: formattedEndDate,
    region
  });

  try {
    const { data, error } = await supabase.functions.invoke('fetch-golfnow-tee-times', {
      body: {
        facilityId: numericFacilityId,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        players,
        holes,
        region
      }
    });

    if (error) {
      console.error('[GolfNow] Edge function error:', error);
      return { success: false, error: error.message, teeTimes: [], summary: null };
    }

    console.log('[GolfNow] Response:', {
      success: data?.success,
      teeTimes: data?.teeTimes?.length || 0,
      timing: data?.timing
    });

    return data;
  } catch (err) {
    console.error('[GolfNow] Exception:', err);
    return { success: false, error: err.message, teeTimes: [], summary: null };
  }
}

/**
 * Convert a date value to YYYY-MM-DD string format
 * @param {Date|string} date - Date object or string
 * @returns {string|null} Date in YYYY-MM-DD format
 */
function formatDateToYYYYMMDD(date) {
  if (!date) return null;
  if (typeof date === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
    const parsed = new Date(date);
    if (!isNaN(parsed.getTime())) {
      return toLocalDateKey(parsed);
    }
    return null;
  }
  if (date instanceof Date && !isNaN(date.getTime())) {
    return toLocalDateKey(date);
  }
  return null;
}

/**
 * Add minutes to a time string
 * @param {string} time - Time in HH:MM format
 * @param {number} minutes - Minutes to add
 * @returns {string} New time in HH:MM format
 */
function addMinutesToTime(time, minutes) {
  const [hours, mins] = time.split(':').map(Number);
  const totalMinutes = hours * 60 + mins + minutes;
  const newHours = Math.floor(totalMinutes / 60) % 24;
  const newMins = totalMinutes % 60;
  return `${String(newHours).padStart(2, '0')}:${String(newMins).padStart(2, '0')}`;
}

/**
 * Find the closest weather result for a given date and time
 * @param {Array} weatherResults - Array of results with weather data
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} time - Time in HH:MM format
 * @returns {Object|null} The closest matching weather result
 */
function findClosestWeatherResult(weatherResults, date, time) {
  if (!weatherResults?.length) return null;

  // First try exact date match
  const sameDayResults = weatherResults.filter(r => {
    const resultDate = r.date || r.datetime?.split('T')[0];
    return resultDate === date;
  });

  if (sameDayResults.length === 0) {
    return weatherResults[0]; // fallback
  }

  if (sameDayResults.length === 1) {
    return sameDayResults[0];
  }

  // Multiple same-day results - find closest by time
  const [targetHour, targetMin] = time.split(':').map(Number);
  const targetMinutes = targetHour * 60 + targetMin;

  let closest = sameDayResults[0];
  let closestDiff = Infinity;

  for (const result of sameDayResults) {
    const resultTime = result.window?.start || '12:00';
    const [resultHour, resultMin] = resultTime.split(':').map(Number);
    const resultMinutes = resultHour * 60 + resultMin;
    const diff = Math.abs(resultMinutes - targetMinutes);

    if (diff < closestDiff) {
      closestDiff = diff;
      closest = result;
    }
  }

  return closest;
}

/**
 * Transform GolfNow tee times into Smart Schedule result objects
 * @param {Array} teeTimes - Array of GolfNow tee times
 * @param {Object} options - Configuration options
 * @param {number} options.holeCount - Number of holes (9 or 18)
 * @param {Array} options.weatherResults - Existing results with weather data
 * @param {Object} options.courseData - Selected course object
 * @param {number} [options.targetPlayers] - Target player count (1 for solo, group size for groups)
 * @param {Object} [options.availabilityData] - Raw member availability data for per-tee-time calculation
 * @param {Array} [options.selectedMemberIds] - Array of member IDs to check availability for
 * @param {number} [options.minAvailableMembers] - Minimum members required (default: 2 for groups, 1 for solo)
 * @returns {Array} Array of Smart Schedule result objects
 */
export function transformGolfNowToResults(teeTimes, options) {
  const {
    holeCount = 18,
    weatherResults = [],
    courseData = null,
    targetPlayers = null,
    availabilityData = null,
    selectedMemberIds = [],
    minAvailableMembers = null
  } = options;

  const roundDuration = holeCount === 9 ? 120 : 240; // minutes

  // Filter tee times by player count if targetPlayers is specified
  let filteredTeeTimes = teeTimes;
  if (targetPlayers !== null && targetPlayers > 0) {
    // First try exact match
    const exactMatch = teeTimes.filter(tt => tt.players === targetPlayers);
    if (exactMatch.length > 0) {
      filteredTeeTimes = exactMatch;
    } else {
      // Fall back to rates that can accommodate the target (players >= target)
      const accommodating = teeTimes.filter(tt => tt.players >= targetPlayers);
      if (accommodating.length > 0) {
        filteredTeeTimes = accommodating;
      }
      // If still no match, show all tee times
    }
  }

  // Debug: Log availability data received
  if (import.meta.env.DEV) {
    console.log('[GolfNow Transform] Received options:', {
      availabilityData: availabilityData ? Object.keys(availabilityData) : null,
      selectedMemberIds,
      targetPlayers,
      minAvailableMembers,
      holeCount
    });
  }

  // Transform tee times to results
  const transformedResults = filteredTeeTimes.map(tt => {
    const startTime = tt.time;
    const endTime = addMinutesToTime(startTime, roundDuration);

    // Find closest weather result to inherit weather data
    const weatherMatch = findClosestWeatherResult(weatherResults, tt.date, tt.time);

    const result = {
      date: tt.date,
      datetime: tt.datetime || `${tt.date}T${tt.time}:00`,
      window: {
        start: startTime,
        end: endTime
      },
      golfnow: {
        exactTime: tt.time,
        price: tt.price,
        currency: tt.currency,
        bookingUrl: tt.bookingUrl,
        isHotDeal: tt.isHotDeal,
        players: tt.players,
        holes: tt.holes,
        rateId: tt.rateId
      },
      golfCourse: courseData,
      // Inherit weather data from closest match
      weather: weatherMatch?.weather || null,
      weatherError: weatherMatch?.weatherError || null,
      compositeScore: weatherMatch?.compositeScore || 5.0,
      daylightCategory: weatherMatch?.daylightCategory || 'unknown'
    };

    // Calculate availability for this specific tee time if availability data provided
    if (availabilityData && Object.keys(availabilityData).length > 0) {
      const timeWindow = { start: startTime, end: endTime };
      const analysis = analyseTimeSlot(tt.date, timeWindow, availabilityData, selectedMemberIds, holeCount);
      result.availableMembers = analysis.availableMembers;
      result.unavailableMembers = analysis.unavailableMembers;
      result.totalMembers = analysis.totalMembers;
    }

    return result;
  });

  // Filter out results with insufficient availability (if availability data was provided)
  if (availabilityData && Object.keys(availabilityData).length > 0) {
    // Default minimum: 2 for groups, 1 for solo
    const minRequired = minAvailableMembers !== null
      ? minAvailableMembers
      : (targetPlayers === 1 ? 1 : 2);

    const filteredResults = transformedResults.filter(result => {
      const availableCount = result.availableMembers?.length || 0;
      return availableCount >= minRequired;
    });

    if (import.meta.env.DEV) {
      console.log('[GolfNow Transform] Filtered results:', {
        before: transformedResults.length,
        after: filteredResults.length,
        minRequired,
        filtered: transformedResults.length - filteredResults.length
      });
    }

    return filteredResults;
  }

  if (import.meta.env.DEV) {
    console.log('[GolfNow Transform] No availability data - returning all', transformedResults.length, 'results');
  }

  return transformedResults;
}

/**
 * Sort results by composite score (highest first), then by date/time as tiebreaker
 * @param {Array} results - Array of result objects
 * @returns {Array} Sorted array
 */
export function sortResultsByDateTime(results) {
  return [...results].sort((a, b) => {
    // Primary sort: by composite score (highest first)
    const scoreA = a.compositeScore || 0;
    const scoreB = b.compositeScore || 0;
    if (scoreB !== scoreA) {
      return scoreB - scoreA;
    }
    // Secondary sort: by date/time (earliest first) as tiebreaker
    const dateA = a.datetime || `${a.date}T${a.window?.start || '00:00'}`;
    const dateB = b.datetime || `${b.date}T${b.window?.start || '00:00'}`;
    return dateA.localeCompare(dateB);
  });
}

// Regional domain mapping
const GOLFNOW_DOMAINS = {
  'US': 'www.golfnow.com',
  'UK': 'www.golfnow.co.uk',
  'IE': 'www.golfnow.ie',
  'EU': 'www.golfnow.eu',
  'AU': 'www.golfnow.com.au',
};

/**
 * Build GolfNow booking URL for a facility and date
 * @param {number} facilityId - GolfNow facility ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} region - Region code ('US', 'UK', 'IE', 'EU', 'AU')
 * @returns {string} GolfNow booking URL
 */
export function buildGolfNowBookingUrl(facilityId, date, region = 'US') {
  const domain = GOLFNOW_DOMAINS[region] || GOLFNOW_DOMAINS['US'];
  const dateObj = new Date(date + 'T12:00:00Z');
  const formattedDate = dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  });
  return `https://${domain}/tee-times/facility/${facilityId}/search#date=${encodeURIComponent(formattedDate)}`;
}

/**
 * Extract a GolfNow facility id from a pasted GolfNow URL.
 *
 * Handles the canonical path shape buildGolfNowBookingUrl produces across the
 * regional domains, e.g.
 *   https://www.golfnow.com/tee-times/facility/12345/search#date=...
 *   https://www.golfnow.co.uk/tee-times/facility/12345
 * and tolerates the older ?facilityId=12345 query form as a fallback.
 *
 * @param {string} url - A GolfNow URL pasted by the user
 * @returns {number|null} The numeric facility id, or null if none found
 */
export function extractGolfNowFacilityId(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();

  // Primary: /facility/{id} anywhere in the path.
  const pathMatch = trimmed.match(/\/facility\/(\d+)/i);
  if (pathMatch) return Number(pathMatch[1]);

  // Fallback: a facilityId query parameter.
  const queryMatch = trimmed.match(/[?&]facilityId=(\d+)/i);
  if (queryMatch) return Number(queryMatch[1]);

  return null;
}

/**
 * Format price for display
 * @param {number} price - Price value
 * @param {string} currency - Currency code (USD, GBP, etc.)
 * @returns {string} Formatted price string
 */
export function formatGolfNowPrice(price, currency = 'USD') {
  if (price == null) return '';

  const currencyMap = {
    'USD': 'en-US',
    'GBP': 'en-GB',
    'EUR': 'de-DE',
    'AUD': 'en-AU'
  };

  const locale = currencyMap[currency] || 'en-US';

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return formatter.format(price);
}

/**
 * Get availability for a specific date from results
 * @param {Array} results - Array of result objects
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Array} Results for that date
 */
export function getResultsForDate(results, date) {
  if (!results || !Array.isArray(results)) return [];
  return results.filter(r => r.date === date);
}

/**
 * Get summary statistics for results
 * @param {Array} results - Array of result objects with golfnow data
 * @returns {Object} Summary with count, minPrice, maxPrice
 */
export function getResultsSummary(results) {
  if (!results || results.length === 0) {
    return { count: 0, minPrice: null, maxPrice: null, currency: null };
  }

  const prices = results
    .map(r => r.golfnow?.price)
    .filter(p => p != null && p > 0);

  return {
    count: results.length,
    minPrice: prices.length > 0 ? Math.min(...prices) : null,
    maxPrice: prices.length > 0 ? Math.max(...prices) : null,
    currency: results[0]?.golfnow?.currency || 'USD'
  };
}
